// Damage-feedback model — PURE. No DOM, no audio, no Babylon, zero imports,
// so tools/hudfeedback.mjs can prove every number in plain Node (the same
// reason src/world/query.mjs and src/game/noise.js are importable offline).
// src/game/stats.js owns the state; src/ui/hud.js owns the pixels; this file
// owns the arithmetic between them and nothing else may re-implement it.
//
// WHY A MODEL AND NOT AN `if` IN stats.damage():
// `damage()` is called from two completely different kinds of source and they
// need opposite treatment.
//   • DISCRETE — one blow: an infected claw (6–11 HP), a wolf bite (7–13), a
//     rifle round (28), an axe (32). One event, one flash, one sound.
//   • TRICKLE — a per-frame slice of a damage-per-second: starvation 1.1 HP/s,
//     thirst 1.6, shock up to 1.5, bled out 4.0, sepsis 0.03–0.09. At 60 fps
//     that is 0.0005–0.07 HP per call, i.e. ~60 calls a second. Flashing on
//     each one would strobe the screen; flashing on none is exactly today's
//     bug (bleeding out and sepsis are invisible). So the trickle is POOLED
//     and released as one soft pulse per TRICKLE_HP of accumulated loss, no
//     more often than TRICKLE_GAP.
//
// Classification is explicit where we know it: every continuous call site
// inside stats.js tags itself (`{ kind:'blood', trickle:true }`). The size
// heuristic below is only the fallback for callers outside this module.

// One blow worth this many HP earns a full-strength flash. Calibrated off the
// real weapon table (weapons.js: rifle 28, axe 32, pistol 35) so a rifle round
// is ~full and the two creature attacks (6–13) land in the lower third.
export const HIT_FULL = 25;

// Fallback discrete/trickle split for untagged callers. The weakest discrete
// attack in the game is the 6 HP infected claw; the fastest trickle is 4 HP/s
// (blood at zero), which only exceeds 1.5 HP in a single call if a frame took
// longer than 375 ms. Nothing in between exists, so the gap is comfortable.
export const DISCRETE_HP = 1.5;

export const TRICKLE_HP = 2.5;     // HP of pooled trickle that earns one pulse
export const TRICKLE_GAP = 2.0;    // s — floor between trickle pulses
export const TRICKLE_MAX_SEV = 0.55; // a pulse must never read as a rifle round
export const SOUND_GAP = 0.11;     // s — two hits inside this share one sound
export const MIN_SEV = 0.12;       // the quietest thing we will still show

// Hard ceiling on the hurt vignette's alpha. THE HUD RULE: no overlay may
// become the brightest cluster in the frame. This one cannot, by two
// independent margins — it is capped here, and its colour is a near-black
// red (see #hurt-vig in hud.js, peak #6a0c06, luma ~31/255), so even at full
// alpha it only ever DARKENS the periphery. It is the same construction as
// the fever haze, which is capped at 0.16 because it is a 15-minute state;
// this one lasts under a second, so it is allowed to be stronger.
export const VIG_MAX = 0.72;

const TAU = Math.PI * 2;
const clamp01 = (v) => (v > 1 ? 1 : v < 0 ? 0 : v);

// wrap to (-PI, PI]
export function wrapPi(a) {
  if (!Number.isFinite(a)) return 0;
  let r = (a + Math.PI) % TAU;
  if (r <= 0) r += TAU;
  return r - Math.PI;
}

// How bad was that? 0..1.
// Two readings, and the WORSE one wins, because they answer different
// questions: `bySize` is "how big a blow was that in absolute HP" and
// `byFraction` is "how much of what I had left did it take". The same 6 HP
// claw is a scratch at 90 health and very nearly the end at 4, and a model
// that only knew the absolute number would show the same flash for both.
export function hurtSeverity(amount, healthAfter = 100) {
  const a = Number(amount);
  if (!(a > 0)) return 0;
  const left = Math.max(0, Number(healthAfter) || 0);
  const bySize = clamp01(a / HIT_FULL);
  const byFraction = clamp01(a / Math.max(a + left, 1));
  return Math.max(MIN_SEV, Math.min(1, Math.max(bySize, byFraction)));
}

// Playback rate for the hurt sample: a heavy blow is lower and slower, a
// scratch is higher and shorter. One take therefore covers the whole range
// instead of sounding identical for a graze and a rifle round.
export function hurtRate(sev) {
  return +(1.18 - 0.36 * clamp01(sev)).toFixed(3);
}

export function hurtVol(sev, trickle = false) {
  const base = 0.22 + 0.46 * clamp01(sev);
  // A pooled pulse is an internal sensation, not an impact, and must stay
  // under the QUIETEST discrete hit so the two can never be confused.
  // The first numbers here (0.6 × base, capped 0.3) failed exactly that: a
  // pulse at TRICKLE_MAX_SEV came out at 0.284 against 0.275 for the
  // faintest real blow — found by tools/hudfeedback.mjs, not by ear.
  // 0.55 × base capped at 0.22 leaves a clear margin at both ends.
  return +(trickle ? Math.min(0.22, base * 0.55) : base).toFixed(3);
}

// Peak alpha and decay time for the vignette.
export function vigAlpha(sev) {
  return +Math.min(VIG_MAX, 0.16 + 0.58 * clamp01(sev)).toFixed(3);
}

export function vigMs(sev) {
  return Math.round(260 + 420 * clamp01(sev));
}

// ---- direction ----------------------------------------------------------
// WHAT THE HIT PATHS ACTUALLY PASS (checked before designing this, because
// the agenda's "the ev.dir every hit path computes" is only true of one of
// them):
//   • infected, local sim and server — infected-sim.mjs:745 sends
//     `dir: atan2(tgt.x - e.x, tgt.z - e.z)`, i.e. the WORLD BEARING THE BLOW
//     TRAVELS, attacker → victim. main.js's zombies.onHit uses it for the
//     block cone and then drops it. This is the one real direction we have.
//   • wolves, server — wolves.mjs:428 sends no dir at all (the wolf's x/z are
//     right there, uncomputed).
//   • PvP, server — mp-server.mjs:908 rebuilds the event field by field and
//     keeps no bearing, though it holds both positions to range-check the claim.
//   • wolves, local — main.js's animals.onBite has the whole animal record and
//     passes nothing.
//   • starvation, thirst, blood loss, poison, sepsis — have no direction and
//     never will.
// So direction is OPTIONAL and the omnidirectional case is the common one:
// `screenAngle` returns null when it does not know, and the HUD then shows the
// vignette with no arc rather than inventing a bearing. Handing a wrong arrow
// to a player under fire is worse than handing them none.
//
// Returns radians, 0 = dead ahead, positive = the player's right, or null.
// `dir` is the bearing the blow travels, so the bearing TO the attacker is
// dir + PI — which is exactly the convention main.js already blocks against
// (`player.heading - (ev.dir + PI)`), so a value that satisfies the block cone
// also points the arc at the right spot.
export function screenAngle(dir, heading) {
  if (!Number.isFinite(dir) || !Number.isFinite(heading)) return null;
  return wrapPi(dir + Math.PI - heading);
}

// CSS rotation for the arc, in degrees. 0 keeps the wedge at the top of the
// screen; CSS rotate() is clockwise and `heading` grows clockwise from +Z
// (atan2(dx, dz) with +X to the right), so a positive screen angle — an
// attacker on the player's right — rotates the wedge to the right edge.
export function arcDeg(ang) {
  return Number.isFinite(ang) ? +((ang * 180) / Math.PI).toFixed(1) : 0;
}

// ---- the feed -----------------------------------------------------------
// One instance per Stats. `report()` is called for EVERY damage call and
// returns either null (absorbed into the pool) or the one event the HUD and
// the mixer should act on. Times are ms, from the caller's clock, so the
// harness can drive it without a real timer.
export class HurtFeed {
  constructor() {
    this.pool = 0;        // HP of trickle banked but not yet shown
    this.poolT = -1e9;    // ms of the last trickle pulse
    this.soundT = -1e9;   // ms of the last hurt sound
    this.lastSev = 0;     // severity of the last event emitted (for tests)
  }

  reset() {
    this.pool = 0;
    this.poolT = -1e9;
    this.soundT = -1e9;
    this.lastSev = 0;
  }

  // amount: HP lost. now: ms. healthAfter: HP remaining after the subtraction.
  // src: null, or { kind, trickle, dir, heading }.
  report(amount, now, healthAfter = 100, src = null) {
    const a = Number(amount);
    if (!(a > 0) || !Number.isFinite(now)) return null;
    const kind = (src && src.kind) || null;
    const trickle = src && typeof src.trickle === 'boolean' ? src.trickle : a < DISCRETE_HP;

    if (trickle) {
      this.pool += a;
      if (this.pool < TRICKLE_HP) return null;
      if (now - this.poolT < TRICKLE_GAP * 1000) return null; // keep banking
      const shown = this.pool;
      this.pool = 0;
      this.poolT = now;
      const sev = Math.min(TRICKLE_MAX_SEV, hurtSeverity(shown, healthAfter));
      return this._emit({ sev, ang: null, kind, trickle: true, amount: shown, now });
    }

    // A discrete hit always shows. Only the SOUND is rate-limited: a burst or
    // two pellets in the same frame should read as one louder impact, not as
    // two samples on top of each other — but the vignette must still deepen,
    // which it does because the HUD takes the max of the live flash and the
    // new one.
    const sev = hurtSeverity(a, healthAfter);
    const ang = screenAngle(src && src.dir, src && src.heading);
    return this._emit({ sev, ang, kind, trickle: false, amount: a, now });
  }

  _emit(e) {
    const sound = e.now - this.soundT >= SOUND_GAP * 1000;
    if (sound) this.soundT = e.now;
    this.lastSev = e.sev;
    return {
      sev: e.sev,
      ang: e.ang,
      deg: arcDeg(e.ang),
      kind: e.kind,
      trickle: e.trickle,
      amount: +e.amount.toFixed(3),
      sound,
      rate: hurtRate(e.sev),
      vol: hurtVol(e.sev, e.trickle),
      alpha: vigAlpha(e.sev),
      ms: vigMs(e.sev),
    };
  }
}

export default HurtFeed;
